import * as React from 'react';
import { QueryClientProvider } from 'react-query';
import 'semantic-ui-css/semantic.min.css';

import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/ar';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/pt';

import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/ar';
import '@formatjs/intl-relativetimeformat/locale-data/en';
import '@formatjs/intl-relativetimeformat/locale-data/pt';
import { initMonitoring } from './shared/services/monitoring';
import { initStore } from './shared/services/store';
import { I18nContext, useLanguage } from './shared/services/i18n';
import { initFetchInterceptor } from './shared/services/fetchInterceptor';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { PUBLIC_URL } from './config';
import { App, ErrorBoundary } from './containers';
import './index.scss';
import { IntlProvider } from 'react-intl';
import * as serviceWorker from './serviceWorker';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { queryClient } from './shared/api/apiHooks';

const sentryReduxEnhancer = initMonitoring();
const store = initStore(sentryReduxEnhancer);
// Install the fetch interceptor once at app boot, before any API call fires.
initFetchInterceptor();

export const Root = () => {
  const { language, messages, setLanguage } = useLanguage();
  return (
    <Provider store={store}>
      <I18nContext.Provider value={{ language, setLanguage }}>
        <IntlProvider
          locale={language}
          key={language}
          messages={messages}
          onError={err => {
            // Ignore missing translation errors for now since we have a lot of them and they are not critical. We can revisit this later.
            // Otherwise it will throw so many errors in the console and hang the app.
            if (err.code === 'MISSING_TRANSLATION') {
              return;
            }
            console.debug(err);
          }}
        >
          <Router basename={PUBLIC_URL}>
            <ErrorBoundary showUI={true}>
              <QueryClientProvider client={queryClient}>
                <App />
              </QueryClientProvider>
            </ErrorBoundary>
          </Router>
        </IntlProvider>
      </I18nContext.Provider>
    </Provider>
  );
};

export const renderApp = () => {
  const rootElement = document.getElementById('root');
  if (rootElement?.hasChildNodes()) {
    hydrateRoot(rootElement, <Root />);
  } else {
    const root = createRoot(rootElement!);
    root.render(<Root />);
  }
};

// If you want your app to work offline and load faster, you can change
// Unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
